import React, { useState } from 'react';
import { RichTextProps } from '../../RichText';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Text } from '@withjoy/joykit/icons';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { LayoutEditComponentListItem } from '@website-editor/features/custom-page/components/LeftPanelLayouts/LayoutEditComponentListItem';
import { RICH_TEXT_ACTIONS } from '../../RichText.constants';
import { TextComponentFragment } from '@graphql/generated';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { getFirstHtmlElement } from '@shared/utils/html';

export const RichTextEditListItem: React.FC<RichTextProps> = props => {
  const { componentData, isLastItem, sortable } = props;
  const [isHover, setIsHover] = useState(false);
  const { tq } = useTranslation('websiteEditor');
  const { navigateToScreen } = useNavigationPaneContext();
  const { setCustomComponentBlockData, setShowDeletePopup, setDeleteComponentDetails } = useCustomComponentStateContext<TextComponentFragment>();

  const title = getFirstHtmlElement(componentData?.text);
  const subTitle = getFirstHtmlElement(componentData?.text, 'p');

  const handleEdit = useEventCallback(() => {
    setCustomComponentBlockData(componentData);
    navigateToScreen(RICH_TEXT_ACTIONS.RICH_TEXT_EDIT);
  });

  const handleDeleteClick = useEventCallback(() => {
    if (componentData?.id) {
      setShowDeletePopup(true);
      setDeleteComponentDetails({
        id: componentData.id,
        title: tq`Are you sure you want to delete this text block?`,
        subTitle: tq`The contents of this section will be lost. This action can’t be undone.`,
        deleteCta: tq`Delete Text Block`,
        cancelCta: tq`Cancel`,
        successMessage: tq`Success! Text has been deleted.`,
        errorMessage: tq`An error occurred while deleting your text. Please try again.`,
      });
    }
  });

  return (
    <LayoutEditComponentListItem
      isDragActive={sortable?.isDragActive ?? false}
      dragItemId={sortable?.dragItemId ?? ''}
      isLastItem={isLastItem}
      height={sortable?.itemSize}
      itemId={componentData.id}
      icon={<Text size={32} />}
      isHover={isHover}
      setIsHover={setIsHover}
      handleEdit={handleEdit}
      handleDelete={handleDeleteClick}
      title={
        <>
          {title ? (
            <Box
              width="100%"
              className="jodit-list"
              flexDirection="column"
              css={{ ['a']: { 'pointer-events': 'none !important' } }}
              color={isHover ? 'linkHover' : 'mono14'}
              _groupActive={{ color: 'linkActive' }}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          ) : (
            <>
              {!subTitle && (
                <TextV2
                  typographyVariant="button2"
                  color={isHover ? 'linkHover' : 'mono14'}
                  _groupActive={{ color: 'linkActive' }}
                >
                  {tq`Text`}
                </TextV2>
              )}
            </>
          )}
        </>
      }
      subTitle={
        <>
          {subTitle ? (
            <Flex
              width="100%"
              className="jodit-list"
              flexDirection="column"
              css={{ all: 'revert', ['a']: { 'pointer-events': 'none !important' } }}
              dangerouslySetInnerHTML={{ __html: subTitle }}
            />
          ) : (
            <>
              {!title && (
                <TextV2
                  typographyVariant="body1"
                  color="mono12"
                >
                  {tq`Tell your guests what to expect here—make it fun, exciting, and uniquely you.`}
                </TextV2>
              )}
            </>
          )}
        </>
      }
    />
  );
};
